import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { trackEvent } from "../utils/analytics";
import FadeInSection from "./FadeInSection";

const LINKEDIN_URL = "https://www.linkedin.com/company/thrombioholdings";

export default function Header() {
  const location = useLocation().pathname;

  // On the homepage the nav drops in after the hero logo and brain animations.
  const isHome = location === "/";
  const NavReveal = isHome ? FadeInSection : "div";
  const navRevealProps = isHome ? { direction: "down", playOnLoad: true } : {};
  const navRevealDelay = isHome ? " delay-[2500ms]" : "";

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [heroLogoInView, setHeroInView] = useState(false);
  const [isTechnologyAccordionOpen, setIsTechnologyAccordionOpen] = useState(false);
  const [isLeadershipAccordionOpen, setIsLeadershipAccordionOpen] = useState(false);
  const [isClinicalTrialAccordionOpen, setIsClinicalTrialAccordionOpen] = useState(false);
  const [isContactAccordionOpen, setIsContactAccordionOpen] = useState(false);
  const [isTechnologyDropdownOpen, setIsTechnologyDropdownOpen] = useState(false);
  const [isLeadershipDropdownOpen, setIsLeadershipDropdownOpen] = useState(false);
  const [isClinicalTrialDropdownOpen, setIsClinicalTrialDropdownOpen] = useState(false);
  const [isContactDropdownOpen, setIsContactDropdownOpen] = useState(false);
  const techologyDropdownRef = useRef(null);
  const leadershipDropdownRef = useRef(null);
  const clinicalTrialDropdownRef = useRef(null);
  const contactDropdownRef = useRef(null);

  const handleClickOutside = (event) => {
    if (techologyDropdownRef.current && !techologyDropdownRef.current.contains(event.target)) {
      setIsTechnologyDropdownOpen(false);
    }
    if (leadershipDropdownRef.current && !leadershipDropdownRef.current.contains(event.target)) {
      setIsLeadershipDropdownOpen(false);
    }
    if (clinicalTrialDropdownRef.current && !clinicalTrialDropdownRef.current.contains(event.target)) {
      setIsClinicalTrialDropdownOpen(false);
    }
    if (contactDropdownRef.current && !contactDropdownRef.current.contains(event.target)) {
      setIsContactDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const heroLogo = document.getElementById("heroLogo");

    if (!heroLogo) {
      setHeroInView(false)
      return
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setHeroInView(true)
          } else {
            setHeroInView(false)
          }
        });
      },
      {
        threshold: 0.1 // Adjust as needed (e.g., 0 = any visibility, 1 = fully in view)
      }
    );

    observer.observe(heroLogo);

    return () => {
      observer.disconnect();
    };
  }, [location]);

  return (
    <div className={`bg-primary/35 backdrop-blur-2xl text-[1.8rem] font-medium max-w-dvw sticky top-0 left-0 z-10`}>
      <div
        className={`max-xl:px-10 page-width flex flex-nowrap max-xl:justify-between gap-32 xl:gap-40 items-center min-h-40 nav text-white`}
      >
<ScrollLink to="/">
<img src="/thromBioInvert.png" alt="ThromBio Logo" height={40} className={`h-[55px] lg:h-20 transition-all duration-500 ${heroLogoInView ? "opacity-0" :"opacity-100"}`} />
        </ScrollLink>
        <NavReveal className={`lg:hidden${navRevealDelay}`} {...navRevealProps}>
          <div onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-12 h-12"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </div>
        </NavReveal>
        <NavReveal
          className={`hidden lg:flex flex-nowrap justify-between flex-grow items-center${navRevealDelay}`}
          {...navRevealProps}
        >
          <ScrollLink
            className={location.startsWith("/about") ? "font-sans font-bold text-primary-red" : undefined}
            to="/about"
          >
            About
          </ScrollLink>
          <div className="flex flex-col items-center relative hover:text-primary-red transition-all">
            <div
              ref={leadershipDropdownRef}
              onClick={() => setIsLeadershipDropdownOpen(!isLeadershipDropdownOpen)}
              className="cursor-pointer"
            >
              <div className="flex gap-2 xl:gap-4 items-center justify-center cursor-pointer">
                <span
                  className={`${location.startsWith("/leadership") ? "font-sans font-bold text-primary-red" : undefined}`}
                >
                  Leadership
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className={`w-8 h-8 transition-all ${isLeadershipDropdownOpen ? "rotate-180" : ""}`}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
              </div>
              <div
                className={`-top-6 left-1/2 -translate-x-1/2 transition-all absolute bg-primary/90 backdrop-blur-2xl shadow z-20 ${isLeadershipDropdownOpen ? "scale-y-100 translate-y-1/2" : "scale-y-0 opacity-0 translate-y-0"}`}
              >
                <div className="flex flex-col divide-y justify-between py-2 text-white">
                  <div className="flex items-center min-h-16 px-4">
                    <ScrollLink
                      to="/leadership/board-of-directors"
                      className={`whitespace-nowrap ${location.startsWith("/leadership/board-of-directors") ? "text-primary-red" : ""}`}
                    >
                      Board of Directors
                    </ScrollLink>
                  </div>
                  <div className="flex items-center min-h-16 px-4">
                    <ScrollLink
                      to="/leadership/senior-management"
                      className={`whitespace-nowrap ${location.startsWith("/leadership/senior-management") ? "text-primary-red" : ""}`}
                    >
                      Senior Management
                    </ScrollLink>
                  </div>
                  <div className="flex items-center min-h-16 px-4">
                    <ScrollLink
                      to="/leadership/corporate-advisors"
                      className={`whitespace-nowrap ${location.startsWith("/leadership/corporate-advisors") ? "text-primary-red" : ""}`}
                    >
                      Corporate Advisors
                    </ScrollLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col relative hover:text-primary-red transition-all">
            <div
              ref={techologyDropdownRef}
              onClick={() => setIsTechnologyDropdownOpen(!isTechnologyDropdownOpen)}
              className="cursor-pointer"
            >
              <div className="flex gap-2 xl:gap-4 items-center cursor-pointer">
                <span
                  className={`${location.startsWith("/technology") ? "font-sans font-bold text-primary-red" : undefined}`}
                >
                  Technology
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className={`w-8 h-8 transition-all ${isTechnologyDropdownOpen ? "rotate-180" : ""}`}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
              </div>
              <div
                className={`-top-6 left-1/2 -translate-x-1/2 transition-all absolute bg-primary/90 backdrop-blur-2xl shadow z-20 ${isTechnologyDropdownOpen ? "scale-y-100 translate-y-1/2" : "scale-y-0 opacity-0 translate-y-0"}`}
              >
                <div className="flex flex-col divide-y justify-between py-2 text-white">
                  <div className="flex items-center min-h-16 px-4">
                    <ScrollLink
                      to="/technology/candidate"
                      className={`whitespace-nowrap ${location.startsWith("/technology/candidate") ? "text-primary-red" : ""}`}
                    >
                      Our Lead Candidate
                    </ScrollLink>
                  </div>
                  <div className="flex items-center min-h-16 px-4">
                    <ScrollLink
                      to="/technology/leadIndication"
                      className={`whitespace-nowrap ${location.startsWith("/technology/leadIndication") ? "text-primary-red" : ""}`}
                    >
                      Lead Indications
                    </ScrollLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center relative hover:text-primary-red transition-all">
            <div
              ref={clinicalTrialDropdownRef}
              onClick={() => setIsClinicalTrialDropdownOpen(!isClinicalTrialDropdownOpen)}
              className="cursor-pointer"
            >
              <div className="flex gap-2 xl:gap-4 items-center justify-center cursor-pointer">
                <span
                  className={`${location.startsWith("/trials") ? "font-sans font-bold text-primary-red" : undefined}`}
                >
                  Clinical Trial
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className={`w-8 h-8 transition-all ${isClinicalTrialDropdownOpen ? "rotate-180" : ""}`}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
              </div>
              <div
                className={`-top-6 left-1/2 -translate-x-1/2 transition-all absolute bg-primary/90 backdrop-blur-2xl shadow z-20 ${isClinicalTrialDropdownOpen ? "scale-y-100 translate-y-1/2" : "scale-y-0 opacity-0 translate-y-0"}`}
              >
                <div className="flex flex-col divide-y justify-between py-2 text-white">
                  <div className="flex items-center min-h-16 px-4">
                    <ScrollLink
                      to="/trials/stars"
                      className={`whitespace-nowrap ${location.startsWith("/trials/stars") ? "text-primary-red" : ""}`}
                    >
                      STARS
                    </ScrollLink>
                  </div>
                  <div className="flex items-center min-h-16 px-4">
                    <ScrollLink
                      to="/trials/costars"
                      className={`whitespace-nowrap ${location.startsWith("/trials/costars") ? "text-primary-red" : ""}`}
                    >
                      CoSTARS
                    </ScrollLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <ScrollLink
            className={location.startsWith("/publications") ? "font-sans font-bold text-primary-red" : undefined}
            to="/publications"
          >
            Publications
          </ScrollLink>
          <ScrollLink
            className={location.startsWith("/media") ? "font-sans font-bold text-primary-red" : undefined}
            to="/media"
          >
            Media
          </ScrollLink>
          <div className="flex flex-col items-center relative hover:text-primary-red transition-all">
            <div
              ref={contactDropdownRef}
              onClick={() => setIsContactDropdownOpen(!isContactDropdownOpen)}
              className="cursor-pointer"
            >
              <div className="flex gap-2 xl:gap-4 items-center justify-center cursor-pointer">
                <span
                  className={`${location.startsWith("/contact") ? "font-sans font-bold text-primary-red" : undefined}`}
                >
                  Contact
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className={`w-8 h-8 transition-all ${isContactDropdownOpen ? "rotate-180" : ""}`}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
              </div>
              <div
                className={`-top-6 left-1/2 -translate-x-1/2 transition-all absolute bg-primary/90 backdrop-blur-2xl shadow z-20 ${isContactDropdownOpen ? "scale-y-100 translate-y-1/2" : "scale-y-0 opacity-0 translate-y-0"}`}
              >
                <div className="flex flex-col divide-y justify-between py-2 text-white">
                  <div className="flex items-center min-h-16 px-4">
                    <ScrollLink
                      to="/contact"
                      className={`whitespace-nowrap ${location.startsWith("/contact") ? "text-primary-red" : ""}`}
                    >
                      Contact Us
                    </ScrollLink>
                  </div>
                  <div className="flex items-center min-h-16 px-4">
                    <a
                      href={LINKEDIN_URL}
                      target="_blank"
                      rel="noreferrer"
                      data-track="manual"
                      onClick={() => trackEvent('outbound_click', { link_url: LINKEDIN_URL, link_name: 'linkedin', link_location: 'header_desktop' })}
                      className="flex items-center gap-2 whitespace-nowrap hover:text-primary-red transition-colors"
                    >
                      Social
                      <img
                        src="/linkedin-icon.png"
                        alt="LinkedIn"
                        className="w-9 h-9"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </NavReveal>
      </div>
      <div
        className={`${location === "/" ? "bg-primary text-white" : "bg-white shadow-inner"} ${isMobileMenuOpen ? "" : "scale-y-0 -transalate-y-1/2"} transition-all max-w-dvw sticky top-0 left-0 z-10 flex flex-col nav mobileNav`}
      >
        <div
          className={`bg-primary/90 backdrop-blur-2xl bg-primary text-white border-red absolute w-full flex flex-col nav mobileNav max-xl:px-10 border-b`}
        >
          <ScrollLink
            onClick={() => setIsMobileMenuOpen(false)}
            className={location.startsWith("/about") ? "font-sans font-bold text-primary-red" : undefined}
            to="/about"
          >
            About
          </ScrollLink>
          <div className="flex flex-col relative mobileNavAccordion">
            <div onClick={() => setIsLeadershipAccordionOpen(!isLeadershipAccordionOpen)} className="cursor-pointer">
              <div className="flex gap-2 xl:gap-4 items-center cursor-pointer">
                <span
                  className={`${location.startsWith("/leadership") ? "font-sans font-bold text-primary-red" : undefined}`}
                >
                  Leadership
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className={`w-8 h-8 transition-all ${isLeadershipAccordionOpen ? "rotate-180" : ""}`}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
              </div>
              <div
                className={`flex flex-col divide-y justify-between transition-all  ${isLeadershipAccordionOpen ? "scale-y-100 py-2" : "scale-y-0 -translate-y-1/2 max-h-0 opacity-0"}`}
              >
                <div
                  className={`${location.includes("board-of-directors") ? "text-primary-red" : ""} flex items-center min-h-16 px-4`}
                >
                  <ScrollLink
                    to="/leadership/board-of-directors"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`whitespace-nowrap ${location.startsWith("/leadership/board-of-directors") ? "text-primary-red" : ""}`}
                  >
                    Board of Directors
                  </ScrollLink>
                </div>
                <div
                  className={`${location.includes("senior-management") ? "text-primary-red" : ""} flex items-center min-h-16 px-4`}
                >
                  <ScrollLink
                    to="/leadership/senior-management"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="whitespace-nowrap"
                  >
                    Senior Management
                  </ScrollLink>
                </div>
                <div
                  className={`${location.includes("corporate-advisors") ? "text-primary-red" : ""} flex items-center min-h-16 px-4`}
                >
                  <ScrollLink
                    to="/leadership/corporate-advisors"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="whitespace-nowrap"
                  >
                    Corporate Advisors
                  </ScrollLink>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col relative mobileNavAccordion">
            <div onClick={() => setIsTechnologyAccordionOpen(!isTechnologyAccordionOpen)} className="cursor-pointer">
              <div className="flex gap-2 xl:gap-4 items-center cursor-pointer">
                <span
                  className={`${location.startsWith("/technology") ? "font-sans font-bold text-primary-red" : undefined}`}
                >
                  Technology
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className={`w-8 h-8 transition-all ${isTechnologyAccordionOpen ? "rotate-180" : ""}`}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
              </div>
              <div
                className={`flex flex-col divide-y justify-between transition-all  ${isTechnologyAccordionOpen ? "scale-y-100 py-2" : "scale-y-0 -translate-y-1/2 max-h-0 opacity-0"}`}
              >
                <div
                  className={`${location.includes("candidate") ? "text-primary-red" : ""} flex items-center min-h-16 px-4`}
                >
                  <ScrollLink
                    to="/technology/candidate"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`whitespace-nowrap ${location.startsWith("/technology/candidate") ? "text-primary-red" : ""}`}
                  >
                    Our Leading Candidate
                  </ScrollLink>
                </div>
                <div
                  className={`${location.includes("leadIndication") ? "text-primary-red" : ""} flex items-center min-h-16 px-4`}
                >
                  <ScrollLink
                    to="/technology/leadIndication"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="whitespace-nowrap"
                  >
                    Lead Indications
                  </ScrollLink>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col relative mobileNavAccordion">
            <div onClick={() => setIsClinicalTrialAccordionOpen(!isClinicalTrialAccordionOpen)} className="cursor-pointer">
              <div className="flex gap-2 xl:gap-4 items-center cursor-pointer">
                <span
                  className={`${location.startsWith("/trials") ? "font-sans font-bold text-primary-red" : undefined}`}
                >
                  Clinical Trial
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className={`w-8 h-8 transition-all ${isClinicalTrialAccordionOpen ? "rotate-180" : ""}`}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
              </div>
              <div
                className={`flex flex-col divide-y justify-between transition-all  ${isClinicalTrialAccordionOpen ? "scale-y-100 py-2" : "scale-y-0 -translate-y-1/2 max-h-0 opacity-0"}`}
              >
                <div
                  className={`${location.includes("/trials/stars") ? "text-primary-red" : ""} flex items-center min-h-16 px-4`}
                >
                  <ScrollLink
                    to="/trials/stars"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`whitespace-nowrap ${location.startsWith("/trials/stars") ? "text-primary-red" : ""}`}
                  >
                    STARS
                  </ScrollLink>
                </div>
                <div
                  className={`${location.includes("/trials/costars") ? "text-primary-red" : ""} flex items-center min-h-16 px-4`}
                >
                  <ScrollLink
                    to="/trials/costars"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="whitespace-nowrap"
                  >
                    CoSTARS
                  </ScrollLink>
                </div>
              </div>
            </div>
          </div>
          <ScrollLink
            onClick={() => setIsMobileMenuOpen(false)}
            className={location.startsWith("/publications") ? "font-sans font-bold text-primary-red" : undefined}
            to="/publications"
          >
            Publications
          </ScrollLink>
          <ScrollLink
            className={location.startsWith("/media") ? "font-sans font-bold text-primary-red" : undefined}
            onClick={() => setIsMobileMenuOpen(false)}
            to="/media"
          >
            Media
          </ScrollLink>
          <div className="flex flex-col relative mobileNavAccordion">
            <div onClick={() => setIsContactAccordionOpen(!isContactAccordionOpen)} className="cursor-pointer">
              <div className="flex gap-2 xl:gap-4 items-center cursor-pointer">
                <span
                  className={`${location.startsWith("/contact") ? "font-sans font-bold text-primary-red" : undefined}`}
                >
                  Contact
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className={`w-8 h-8 transition-all ${isContactAccordionOpen ? "rotate-180" : ""}`}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
              </div>
              <div
                className={`flex flex-col divide-y justify-between transition-all  ${isContactAccordionOpen ? "scale-y-100 py-2" : "scale-y-0 -translate-y-1/2 max-h-0 opacity-0"}`}
              >
                <div
                  className={`${location.includes("contact") ? "text-primary-red" : ""} flex items-center min-h-16 px-4`}
                >
                  <ScrollLink
                    to="/contact"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`whitespace-nowrap ${location.startsWith("/contact") ? "text-primary-red" : ""}`}
                  >
                    Contact Us
                  </ScrollLink>
                </div>
                <div className="flex items-center min-h-16 px-4">
                  <a
                    href={LINKEDIN_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-track="manual"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      trackEvent('outbound_click', { link_url: LINKEDIN_URL, link_name: 'linkedin', link_location: 'header_mobile' });
                    }}
                    className="flex items-center gap-2 whitespace-nowrap hover:text-primary-red transition-colors"
                  >
                    Social
                    <img 
                      src="/linkedin-icon.png"
                      alt="LinkedIn" 
                      className="w-9 h-9"
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScrollLink({ to, onClick, ...props }) {
  const location = useLocation();
  const handleClick = (event) => {
    if (location.pathname === to) {
      // If the link's destination is the current page, prevent the navigation and scroll to the top.
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (location.hash.includes("clinical-trials")) {
      document.getElementById("clinical-trials").scrollIntoView({behavior:"smooth"})
    }
    if (onClick) {
      onClick(event);
    }
  };

  return <Link to={to} onClick={handleClick} {...props} />;
}